import React from "react";
import ReactGridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Widget, AnalyticsTheme } from "./analyticsState";
import WidgetCard from "./WidgetCard";

const ResponsiveGrid = (ReactGridLayout as any).WidthProvider
  ? (ReactGridLayout as any).WidthProvider(ReactGridLayout)
  : ReactGridLayout;

interface LayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
}

interface Props {
  widgets: Widget[];
  activeWidgetId: string | null;
  isDark: boolean;
  theme: AnalyticsTheme;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onLayoutChange: (layout: LayoutItem[]) => void;
  onDeselect: () => void;
}

const Canvas: React.FC<Props> = ({
  widgets, activeWidgetId, isDark, theme, onSelect, onDelete, onLayoutChange, onDeselect,
}) => {
  const layout: LayoutItem[] = widgets.map((w) => ({
    i: w.id,
    x: w.layout.x,
    y: w.layout.y,
    w: w.layout.w,
    h: w.layout.h,
    minW: 2,
    minH: 2,
    resizeHandles: w.uiConfig.global.resizeAllSides !== false 
      ? ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne']
      : ['se'],
  }));

  const canvasStyle: React.CSSProperties = {
    width: theme.canvasWidth || "100%",
    margin: "0 auto",
    minHeight: "100%",
    backgroundColor: theme.canvasBackgroundColor || "",
  };

  const gridClass = theme.showCanvasGridPattern ? (isDark ? "ca-grid-pattern-dark" : "ca-grid-pattern-light") : "";

  if (widgets.length === 0) {
    return (
      <div className={`ca-canvas ${gridClass}`} style={canvasStyle} onClick={onDeselect}>
        <div className="ca-canvas-empty">
          <i className="fa fa-chart-bar" />
          <h3>Start building your dashboard</h3>
          <p>Click "Add Chart" in the toolbar to add your first visualization to the canvas.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`ca-canvas ${gridClass}`} style={canvasStyle} onClick={onDeselect}>
      <ResponsiveGrid
        className="layout"
        layout={layout}
        cols={12}
        rowHeight={80}
        margin={[16, 16] as [number, number]}
        containerPadding={[0, 0] as [number, number]}
        isDraggable
        isResizable
        resizeHandles={['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne']}
        draggableHandle=".ca-widget-header"
        onLayoutChange={(currentLayout: LayoutItem[]) => onLayoutChange(currentLayout)}
        useCSSTransforms
      >
        {widgets.map((w) => (
          <div key={w.id}>
            <WidgetCard
              widget={w}
              isSelected={w.id === activeWidgetId}
              isDark={isDark}
              onSelect={onSelect}
              onDelete={onDelete}
            />
          </div>
        ))}
      </ResponsiveGrid>
    </div>
  );
};

export default Canvas;
